<template>
  <div class="sp-company-form">
    <v-form ref="form">
      <v-text-field
        class="left-input"
        v-model="firstName"
        @change="formChanged"
        :rules="[formRules.required]"
        :label="$t('registration.client.info.firstname')"
        :placeholder="$t('registration.client.info.firstname.placeholder')"
        dense
        outlined
        required
        autofocus
      />
      <v-text-field
        v-model="lastName"
        @change="formChanged"
        :rules="[formRules.required]"
        :label="$t('registration.client.info.lastname')"
        :placeholder="$t('registration.client.info.lastname.placeholder')"
        dense
        outlined
        required
      />
      <vue-tel-input-vuetify
        v-model="phoneNumber"
        @change="formChanged"
        mode="international"
        :placeholder="this.$t('registration.client.info.phonenumber.placeholder')"
        :label="$t('registration.client.info.phonenumber')"
        :selectLabel="$t('registration.client.info.phonenumber.country')"
        outlined
        dense
        required
      />
      <v-text-field
        @change="formChanged"
        v-model="workEmail"
        :rules="[formRules.email, formRules.required, formRules.endsWithCompanyDomain]"
        :label="$t('registration.client.info.workemail')"
        :placeholder="$t('registration.client.info.workemail.placeholder')"
        dense
        outlined
        required
      />
      <v-text-field
        v-model="password"
        @change="formChanged"
        :type="passwordVisibiltyType"
        :append-icon="passwordVisibiltyIcon"
        @click:append="togglePasswordVisibilty"
        :label="$t('registration.client.info.password')"
        :placeholder="$t('registration.client.info.password.placeholder')"
        :rules="[
          formRules.required,
          formRules.minLength(8, $t('registration.client.info.passwords.min.length')),
          formRules.requiredNumber,
          formRules.requiredLowerCase,
          formRules.requiredUpperCase,
          formRules.requiredSpecialSymbol,
        ]"
        dense
        outlined
        required
      />
      <div class='password-warning'> <img :src="warningIcon" alt="" /> {{ $t('registration.client.info.password.info') }}</div>
      <v-text-field
        @change="formChanged"
        v-model="confirmPassword"
        :type="confirmPasswordVisibiltyType"
        :append-icon="confirmPasswordVisibiltyIcon"
        @click:append="toggleConfirmPasswordVisiblity"
        :label="$t('registration.client.info.password.repeat')"
        :placeholder="$t('registration.client.info.password.repeat.placeholder')"
        :rules="[
          formRules.required,
          formRules.minLength(8, $t('registration.client.info.passwords.min.length')),
          formRules.requiredNumber,
          formRules.requiredLowerCase,
          formRules.requiredUpperCase,
          formRules.requiredSpecialSymbol,
          formRules.matchWithPassword,
        ]"
        dense
        outlined
        required
      />
      <password-strength :strength="passwordStrengthLevel" />
    </v-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { rules, validatePasswordStrength } from '@/utils/validationUtils';
import PasswordStrength from '@/components/registration/PasswordStrength.vue';
import warning from '@/assets/icons/warning_icn@1x.svg';

export default {
  name: 'CompanySPVerificationEmail',
  components: {
    PasswordStrength,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      valid: this.value.valid,
      password: this.value.password,
      workEmail: this.value.workEmail,
      lastName: this.value.lastName,
      firstName: this.value.firstName,
      phoneNumber: this.value.phoneNumber,
      confirmPassword: this.value.confirmPassword,

      showPassword: false,
      showConfirmPassword: false,
    };
  },
  computed: {
    warningIcon() {
      return warning;
    },
    formRules() {
      return {
        ...rules,
        phone: v => rules.phone(v) || this.$t('registration.client.info.phone.number.is.not.valid'),
        email: v => (!!v && rules.email(v.toLowerCase())) || this.$t('registration.client.info.email.is.not.valid'),
        required: v => rules.required(v) || this.$t('required'),
        matchWithPassword: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          v === this.password || this.$t('registration.client.info.passwords.must.be.same'),
        requiredNumber: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredNumber(v) || this.$t('registration.client.info.passwords.at.least.one.number'),
        requiredUpperCase: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredUpperCase(v) || this.$t('registration.client.info.passwords.at.least.one.capital'),
        requiredLowerCase: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredLowerCase(v) || this.$t('registration.client.info.passwords.at.least.one.lower'),
        requiredSpecialSymbol: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredSpecialSymbol(v) || this.$t('registration.client.info.passwords.at.least.one.symbol'),
        endsWithCompanyDomain: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          (!!v && v.toLowerCase().endsWith(this.spCompanyRegistrationMainInfo.organizationDomain)) ||
          this.$t('registration.client.info.email.should.be.in.company.domain'),
      };
    },
    ...mapGetters([
      'spCompanyRegistrationEmailVerificationInfo',
      'spCompanyRegistrationMainInfo',
    ]),
    passwordStrengthLevel() {
      return validatePasswordStrength(this.password);
    },
    passwordVisibiltyIcon() {
      return !this.showPassword ? 'visibility' : 'visibility_off';
    },
    confirmPasswordVisibiltyIcon() {
      return !this.showConfirmPassword ? 'visibility' : 'visibility_off';
    },
    passwordVisibiltyType() {
      return this.showPassword ? 'text' : 'password';
    },
    confirmPasswordVisibiltyType() {
      return this.showConfirmPassword ? 'text' : 'password';
    },
  },
  methods: {
    async formChanged() {
      this.valid = this.$refs.form.inputs.every(input => input.valid);
      await this.$emit('changed', {
        valid: this.valid,
        password: this.password,
        workEmail: this.workEmail,
        confirmPassword: this.confirmPassword,
        lastName: this.lastName,
        firstName: this.firstName,
        phoneNumber: this.phoneNumber,
      });
    },
    togglePasswordVisibilty() {
      this.showPassword = !this.showPassword;
    },
    toggleConfirmPasswordVisiblity() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },
  },
  watch: {
    async confirmPassword() {
      await this.formChanged();
    },
    async password() {
      await this.formChanged();
    },
    async workEmail() {
      await this.formChanged();
    },
    async lastName() {
      await this.formChanged();
    },
    async firstName() {
      await this.formChanged();
    },
    async phoneNumber() {
      await this.formChanged();
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.v-text-field--outlined.v-input--dense > .v-input__control > .v-input__slot {
  height: 32px;
}

.vue-tel-input-vuetify .v-text-field--outlined .v-select__selections .vti__flag {
  margin-left: $space-5 !important;
}

.sp-company-form {
  min-width: 60%;
  .v-input__icon--append {
    margin-top: 0 !important;
  }
  .v-autocomplete {
    input {
      text-overflow: ellipsis;
    }
  }

  .v-text-field__suffix {
    color: $auth-text-primary;
    font-size: $font-size-base;
    letter-spacing: 0;
    line-height: $font-size-md;
    padding-right: $space-2-5;
  }

  .checkbox-title .v-btn {
    padding: 0px $radius-sm;
  }

  .checkbox-title .v-btn__content {
    color: $info-500;
    font-size: $font-size-base;
    letter-spacing: 0;
    line-height: $font-size-md;
    text-transform: capitalize;
  }
}
</style>

<style lang="scss" scoped>
.password-warning {
  color: #b9c6d1;
  font-weight: 500;
}
.sp-freelancer-form {
  display: flex;
  flex-direction: column;
  min-width: 356px;
  .v-input--checkbox {
    margin-top: 0px;
  }

  .v-autocomplete {
    input {
      text-overflow: ellipsis;
    }
  }
}

.sp-freelancer-form-row {
  display: flex;
}

.left-input {
  margin-right: 8px;
}

.checkbox-title {
  display: flex;
  align-items: center;

  span {
    font-size: 14px;
    letter-spacing: 0;
    line-height: 16px;
  }
}
</style>
